import React, { Component } from 'react';
import { Box, Grommet, Text } from 'grommet';
import { Button, message } from 'antd';
import HeadBar from './HeadBar';
import { withAuthenticator } from '@aws-amplify/ui-react';
import { OPS_DATA_URL } from '../config';
import { getLocalUTCTime } from '../utils/CommonUtil';
import { authenticateToken, getAuthEmailFromToken } from '../utils/auth';
import moment from 'moment';

class MaintenanceByLabel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      allMaintenances: [],
      selectedLabel: 'UNLABELED',
      showUnassignedOnly: false
    };
  }

  componentDidMount() {
    this.loadMaintenances();
  }

  loadMaintenances = async () => {
    this.setState({ loading: true });
    try {
      const res = await fetch(`${OPS_DATA_URL}/maintenances`, { method: 'GET' });
      if (!res.ok) {
        message.error('Failed to load maintenances.');
        this.setState({ loading: false });
        return;
      }
      const data = await res.json();
      const planned = (Array.isArray(data) ? data : []).filter((item) => {
        const status = (item.ci_status || '').toUpperCase();
        if (status === 'COMPLETED') return false;
        // Include items that are in active time window or have no strict time requirement
        return true;
      });

      const labels = this.getLabels(planned);
      const selectedLabel = labels.includes(this.state.selectedLabel)
        ? this.state.selectedLabel
        : (labels[0] || 'UNLABELED');

      this.setState({
        loading: false,
        allMaintenances: planned,
        selectedLabel
      });
    } catch (error) {
      message.error('Network error while loading maintenances.');
      this.setState({ loading: false });
    }
  }

  getLabelValue = (item) => (item.label && item.label.trim() ? item.label.trim() : 'UNLABELED');

  getLabels = (items) => {
    const set = new Set(items.map(this.getLabelValue));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }

  assignToMe = async (item) => {
    const token = authenticateToken();
    const currentUser = getAuthEmailFromToken();

    if (!token || !currentUser) {
      message.error('Login session missing. Please sign in again.');
      return;
    }

    const payload = {
      id: item.id,
      farm: item.farm,
      subject: item.subject,
      start_time: item.start_time,
      end_time: item.end_time,
      ci_status: item.ci_status,
      operator: currentUser,
      desc: item.desc,
      upgrade_details: item.upgrade_details,
      change_id: item.change_id,
      label: this.getLabelValue(item),
      assigned_to: currentUser
    };

    try {
      const res = await fetch(`${OPS_DATA_URL}/maintenances`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        message.error('Failed to assign maintenance.');
        return;
      }

      message.success('Assigned to you successfully.');
      this.loadMaintenances();
    } catch (error) {
      message.error('Network error while assigning maintenance.');
    }
  }

  render() {
    const { loading, allMaintenances, selectedLabel, showUnassignedOnly } = this.state;
    const labels = this.getLabels(allMaintenances);
    const selectedItems = allMaintenances.filter((item) => {
      if (this.getLabelValue(item) !== selectedLabel) return false;
      if (showUnassignedOnly && (item.assigned_to || 'Unassigned') !== 'Unassigned') return false;
      return true;
    });
    const sortedSelectedItems = [...selectedItems].sort((a, b) => {
      const left = Number(a.start_time) || 0;
      const right = Number(b.start_time) || 0;
      return left - right;
    });
    const currentUser = getAuthEmailFromToken();

    return (
      <Grommet>
        <HeadBar />
        <Box pad='medium' gap='medium'>
          <Box direction='row' justify='between' align='center' wrap gap='small'>
            <Text size='xlarge' weight='bold'>Planned Maintenance By Label</Text>
          </Box>
          <Box direction='row' justify='center'>
            <Button type='primary' onClick={() => this.props.history.push('/ops')}>Back to Planned Maintenance</Button>
          </Box>
          <Box direction='row' gap='small' wrap>
            {labels.length === 0 ? (
              <Text>No planned maintenances available.</Text>
            ) : labels.map((label) => (
              <Button
                key={label}
                type={selectedLabel === label ? 'primary' : 'default'}
                onClick={() => this.setState({ selectedLabel: label })}
              >
                {label}
              </Button>
            ))}
          </Box>
          <Box direction='row' gap='small'>
            <Button
              type={!showUnassignedOnly ? 'primary' : 'default'}
              onClick={() => this.setState({ showUnassignedOnly: false })}
            >
              All
            </Button>
            <Button
              type={showUnassignedOnly ? 'primary' : 'default'}
              onClick={() => this.setState({ showUnassignedOnly: true })}
            >
              Unassigned Only
            </Button>
          </Box>

          {loading ? (
            <Text>Loading...</Text>
          ) : sortedSelectedItems.length === 0 ? (
            <Text>No planned maintenances for this label.</Text>
          ) : (() => {
            // Group by calendar date of start_time
            const groups = [];
            const seen = {};
            sortedSelectedItems.forEach((item) => {
              const dateKey = moment(Number(item.start_time)).format('YYYY-MM-DD');
              if (!seen[dateKey]) {
                seen[dateKey] = true;
                groups.push({ dateKey, label: moment(Number(item.start_time)).format('dddd, MMMM D, YYYY'), items: [] });
              }
              groups[groups.length - 1].items.push(item);
            });
            return groups.map((group) => (
              <Box key={group.dateKey} gap='small'>
                <Box
                  border={{ side: 'bottom', color: 'brand', size: 'small' }}
                  pad={{ bottom: 'xsmall' }}
                >
                  <Text size='medium' weight='bold' color='brand'>{group.label}</Text>
                </Box>
                <Box
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                    gap: '12px'
                  }}
                >
                  {group.items.map((item) => {
                    const assignedTo = item.assigned_to || 'Unassigned';
                    const isMine = currentUser && assignedTo === currentUser;
                    const isUnassigned = assignedTo === 'Unassigned';
                    return (
                      <Box
                        key={item.id}
                        border={{ color: isUnassigned ? 'status-critical' : 'light-4', size: isUnassigned ? 'medium' : 'xsmall' }}
                        round='xsmall'
                        pad='medium'
                        gap='xsmall'
                        background='white'
                        style={{ minHeight: '240px' }}
                      >
                        <Text weight='bold' size='large'>{item.subject || 'No subject'} ({(item.farm || '').toUpperCase()})</Text>
                        <Text>Label: {this.getLabelValue(item)}</Text>
                        <Text>Start: {getLocalUTCTime(item.start_time)}</Text>
                        <Text>End: {getLocalUTCTime(item.end_time)}</Text>
                        <Text>Status: {item.ci_status || 'N/A'}</Text>
                        <Text>Assigned To: {assignedTo}</Text>
                        <Box direction='row' gap='small' margin={{ top: 'small' }}>
                          <Button
                            disabled={isMine}
                            onClick={() => this.assignToMe(item)}
                          >
                            {isMine ? 'Assigned to me' : 'Assign to me'}
                          </Button>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ));
          })()}
        </Box>
      </Grommet>
    );
  }
}

export default withAuthenticator(MaintenanceByLabel);
